// Prospecting (CRM Phase A, docs/pipeline/CRM-PHASE-A.md): types, queries and small helpers.
// Every /api/prospecting route runs as the database role prospect_rw on the server, so nothing here
// can show deal or borrower data; deal-side views (LO card, broker links) use their own routes.
import { useQueries, useQuery, type QueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { hasCap, hasPipeline, hasProspecting } from "./roles";
import type { Me } from "./types";

export type Workspace = "prospecting" | "scenarios" | "deals";

/**
 * Which workspace a path belongs to (D25: green Prospecting, navy Active deals). Scenarios sit on the
 * deal side of the wall (they hold borrower data), so only pipeline users get them.
 */
export function workspaceOf(pathname: string): Workspace {
  if (pathname === "/prospecting" || pathname.startsWith("/prospecting/")) {
    return "prospecting";
  }
  if (
    pathname === "/scenarios" ||
    pathname.startsWith("/scenarios/") ||
    pathname === "/desk" ||
    pathname.startsWith("/desk/")
  ) {
    return "scenarios";
  }
  return "deals";
}

/** The workspaces this user can open, in switcher order. */
export function workspacesFor(me: Me): Workspace[] {
  const out: Workspace[] = [];
  if (hasProspecting(me)) out.push("prospecting");
  if (hasPipeline(me)) {
    out.push("scenarios");
    out.push("deals");
  }
  return out;
}

export function scenariosNav(me: Me) {
  const items: {
    to: string;
    label: string;
    end?: boolean;
    reloadDocument?: boolean;
    hint?: string;
  }[] = [{ to: "/scenarios", label: "All Scenarios", end: true }];
  if (me.user.desk_available) {
    items.push({
      to: "/desk/",
      label: "Scenario Desk",
      reloadDocument: true,
      hint: "Open the Four-Legs & AI Pricing Desk",
    });
  }
  items.push({ to: "/smart-drops", label: "Smart Drops", end: false });
  if (hasProspecting(me)) {
    items.push({ to: "/prospecting", label: "Prospecting CRM", end: false });
  }
  return items;
}

export function prospectingNav(me: Me) {
  const items = [{ to: "/prospecting", label: "Today", end: true }];
  if (
    hasCap(me, "work_own_relationships") ||
    hasCap(me, "view_team_relationships")
  ) {
    items.push({
      to: "/prospecting/relationships",
      label: "Relationships",
      end: false,
    });
  }
  items.push({ to: "/prospecting/directory", label: "Directory", end: false });
  if (hasCap(me, "manage_crm_directory"))
    items.push({
      to: "/prospecting/review",
      label: "Review queue",
      end: false,
    });
  return items;
}

export interface PersonRow {
  id: string;
  full_name: string;
  nmls: string | null;
  email: string | null;
  phone: string | null;
  company_id: string | null;
  company_name: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
}

export interface CompanyRow {
  id: string;
  name: string;
  nmls: string | null;
  status: string;
  lo_count: number;
  owner_user_id: string | null;
  owner_name: string | null;
}

export interface Activity {
  id: string;
  relationship_id: string;
  kind: ActivityKind;
  occurred_at: string;
  summary: string;
  follow_up_due: string | null;
  follow_up_done_at: string | null;
  actor_id: string;
  actor_name: string | null;
  source: "manual" | "lendy" | "import";
}

export type ActivityKind = "call" | "email" | "note" | "meeting" | "task";
export const ACTIVITY_KINDS: { value: ActivityKind; label: string }[] = [
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Meeting" },
  { value: "note", label: "Note" },
  { value: "task", label: "Task" },
];

export interface DealLink {
  id: string;
  relationship_id: string;
  core_deal_id: string | null;
  created_by: string;
  created_at: string;
  linked_at: string | null;
}

export interface DealSummaryRow {
  deal_ref: string;
  person_id: string | null;
  company_id: string | null;
  product_type: string | null;
  stage_group: string | null;
  status: "open" | "closed";
  target_month: string | null;
  ae_user_id: string;
  ae_name: string | null;
}

export interface RecordCommon {
  owner: {
    relationship_id: string;
    owner_user_id: string;
    owner_name: string;
  } | null;
  relationship: {
    id: string;
    owner_user_id: string;
    owner_team_id?: string | null;
    owner_name: string;
    tier_key: string | null;
    tier_label: string | null;
    started_at: string;
  } | null;
  activities: Activity[];
  deal_links: DealLink[];
  deals: { open: number; closed: number; items: DealSummaryRow[] } | null;
}

export interface Affiliation {
  id: string;
  company_id: string;
  company_name: string;
  company_nmls: string | null;
  started_on: string;
  ended_on: string | null;
  source: string;
}

export interface PersonPage extends RecordCommon {
  person: {
    id: string;
    full_name: string;
    nmls: string | null;
    email: string | null;
    phone: string | null;
    title: string | null;
    sf_do_not_call: boolean | null;
    sf_email_opt_out: boolean | null;
    sf_observed_at: string | null;
    status: string;
    company_id: string | null;
    company_name: string | null;
  };
  affiliations: Affiliation[];
  external_refs: { system: string; value: string }[];
}

export interface CompanyPage extends RecordCommon {
  company: { id: string; name: string; nmls: string | null; status: string };
  people: PersonRow[];
}

export interface RelationshipRow {
  id: string;
  person_id: string | null;
  company_id: string | null;
  name: string;
  company_name: string | null;
  nmls: string | null;
  email: string | null;
  phone: string | null;
  sf_do_not_call: boolean | null;
  sf_email_opt_out: boolean | null;
  owner_user_id: string;
  owner_team_id?: string | null;
  owner_name: string;
  tier_key: string | null;
  tier_label: string | null;
  started_at: string;
  last_touch_at: string | null;
  next_follow_up: string | null;
  open_follow_ups: number;
  deal_links?: DealLink[];
}

export interface FollowUp {
  id: string;
  relationship_id: string;
  person_id: string | null;
  company_id: string | null;
  name: string;
  sf_do_not_call: boolean | null;
  sf_email_opt_out: boolean | null;
  kind: ActivityKind;
  summary: string;
  follow_up_due: string;
  overdue: boolean;
  owner_user_id: string;
  owner_team_id?: string | null;
  owner_name: string;
}

export interface Tier {
  key: string;
  label: string;
  sort: number;
  description: string;
}

export interface ReviewItem {
  id: string;
  kind: "possible_duplicate" | "conflicting_keys";
  entity_kind: string;
  proposed: {
    full_name?: string;
    nmls?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  candidate_ids: string[];
  reason: string;
  batch_id: string | null;
  created_at: string;
}

export interface LoCard {
  person_id: string;
  full_name: string;
  nmls: string | null;
  company_id: string | null;
  company_name: string | null;
  ae_user_id: string | null;
  ae_name: string | null;
  tier: string | null;
  sf_do_not_call: boolean | null;
  sf_email_opt_out: boolean | null;
  sf_observed_at: string | null;
  last_touch_at: string | null;
}

export interface BrokerCandidate {
  company_id: string | null;
  company_name: string | null;
  person_id: string | null;
  full_name: string | null;
  matched: "company_and_lo" | "company" | "lo";
}

export interface BrokerQueueItem {
  loan_id: string;
  broker: string;
  owner_name: string;
  loan_type: string | null;
  stage_key: string | null;
  candidates: BrokerCandidate[];
}

export const crmKeys = {
  all: ["crm"] as const,
  search: (q: string) => ["crm", "search", q] as const,
  person: (id: string) => ["crm", "person", id] as const,
  company: (id: string) => ["crm", "company", id] as const,
  relationships: (scope: "mine" | "book") =>
    ["crm", "relationships", scope] as const,
  followUps: (scope: "mine" | "book", within: number) =>
    ["crm", "follow-ups", scope, within] as const,
  tiers: ["crm", "tiers"] as const,
  review: ["crm", "review"] as const,
  pendingDeals: ["crm", "pending-deals"] as const,
  loCard: (loanId: string) => ["deals", "lo-card", loanId] as const,
  brokerQueue: ["deals", "broker-links"] as const,
};

export function useCrmSearch(q: string, limit = 50, enabled = true) {
  return useQuery({
    queryKey: [...crmKeys.search(q), limit],
    queryFn: ({ signal }) =>
      api<{ people: PersonRow[]; companies: CompanyRow[] }>(
        `/api/prospecting/search?q=${encodeURIComponent(q)}&limit=${limit}`,
        { signal },
      ),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function usePerson(id: string) {
  return useQuery({
    queryKey: crmKeys.person(id),
    queryFn: () => api<PersonPage>(`/api/prospecting/people/${id}`),
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: crmKeys.company(id),
    queryFn: () => api<CompanyPage>(`/api/prospecting/companies/${id}`),
  });
}

export function useRelationships(scope: "mine" | "book", enabled = true) {
  return useQuery({
    queryKey: crmKeys.relationships(scope),
    queryFn: () =>
      api<RelationshipRow[]>(`/api/prospecting/relationships?scope=${scope}`),
    enabled,
  });
}

export function useFollowUps(
  scope: "mine" | "book",
  within = 0,
  enabled = true,
) {
  return useQuery({
    queryKey: crmKeys.followUps(scope, within),
    queryFn: () =>
      api<FollowUp[]>(
        `/api/prospecting/follow-ups?scope=${scope}&within_days=${within}`,
      ),
    enabled,
  });
}

export function useTiers() {
  return useQuery({
    queryKey: crmKeys.tiers,
    queryFn: () => api<Tier[]>("/api/prospecting/tiers"),
    staleTime: 5 * 60_000,
  });
}

export function useReviewQueue(enabled: boolean) {
  return useQuery({
    queryKey: crmKeys.review,
    queryFn: () => api<ReviewItem[]>("/api/prospecting/review"),
    enabled,
  });
}

export function useLoCard(loanId: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: crmKeys.loCard(loanId),
    queryFn: () =>
      api<{
        card: LoCard | null;
        company: {
          company_id: string;
          company_name: string;
          nmls: string | null;
        } | null;
      }>(`/api/loans/${loanId}/lo-card`),
    retry: false,
  });
}

export function useBrokerQueue() {
  return useQuery({
    queryKey: crmKeys.brokerQueue,
    queryFn: () => api<BrokerQueueItem[]>("/api/broker-links"),
  });
}

function parseYmd(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const d = new Date(y, mo - 1, da);
  if (
    Number.isNaN(d.getTime()) ||
    d.getFullYear() !== y ||
    d.getMonth() !== mo - 1 ||
    d.getDate() !== da
  )
    return null;
  return d;
}

/** "2026-10-17" → "Oct 17, 2026" in the person's local time zone; ISO timestamps get a date only. */
export function shortDate(value: string | null | undefined): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const ymdDate = parseYmd(value);
    if (!ymdDate) return value;
    return ymdDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Formats a brokerage affiliation calendar date (`started_on` / `ended_on`) in the person's local
 * time zone. The server writes default `started_on` using PostgreSQL `current_date` (UTC), which is
 * tomorrow's calendar date during US evening hours while `relationship.started_at` (`timestamptz`)
 * formats as local today. Clamp a server-UTC date that is ahead of local today back to local today.
 */
export function affiliationDate(
  value: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value) && parseYmd(value)) {
    const localToday = ymd(now);
    return shortDate(value > localToday ? localToday : value);
  }
  return shortDate(value);
}

/** SSN-shaped text in a note: warn (borrower details belong on the deal side), never block. */
export function looksLikeBorrowerIdentifier(text: string): boolean {
  return /(^|[^0-9])(\d{3}-\d{2}-\d{4}|\d{9})(?![0-9])/.test(text);
}

/** One Salesforce channel flag as last observed: true blocks, false allows, missing is unknown. */
export type ChannelState = "blocked" | "unknown" | "allowed";

export function channelState(flag: boolean | null | undefined): ChannelState {
  return flag === true ? "blocked" : flag === false ? "allowed" : "unknown";
}

interface ChannelFields {
  sf_do_not_call: boolean | null;
  sf_email_opt_out: boolean | null;
}

/**
 * Salesforce channel flags as last observed; Salesforce stays authoritative. Unknown is shown, never
 * hidden: an unknown flag is a restriction (owner rule), not a clear channel.
 */
export function channelFlags(p: ChannelFields): string[] {
  const out: string[] = [];
  const call = channelState(p.sf_do_not_call);
  if (call === "blocked") out.push("Do Not Call");
  else if (call === "unknown") out.push("Call status unknown");
  const email = channelState(p.sf_email_opt_out);
  if (email === "blocked") out.push("Email Opt Out");
  else if (email === "unknown") out.push("Email status unknown");
  return out;
}

/** Only an observed "Do Not Call: off" clears a call; true or unknown holds it. */
export function mayCall(p: ChannelFields): boolean {
  return channelState(p.sf_do_not_call) === "allowed";
}

export function matchLabel(m: BrokerCandidate["matched"]): string {
  return m === "company_and_lo"
    ? "Company and LO match"
    : m === "company"
      ? "Company matches"
      : "LO name matches";
}

/** Returns YYYY-MM-DD offset by `days` from today (or from `base` YYYY-MM-DD if provided). */
export function datePlusDays(days: number, base?: string | null): string {
  const d = (base ? parseYmd(base) : null) ?? new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Path to the Four Legs Desk (D22: reloadDocument navigation, no invented query parameters). */
export const DESK_HREF = "/desk/";

/** Deal links that have not yet been attached to a core deal (`core_deal_id` is null). */
export function pendingDealLinks(
  links: readonly DealLink[] | null | undefined,
): DealLink[] {
  return (links ?? []).filter((l) => l.core_deal_id === null);
}

/** Human-readable count label for pending deal links ("1 deal waiting for a scenario"). */
export function pendingDealsLabel(count: number): string {
  return `${count} ${count === 1 ? "deal" : "deals"} waiting for a scenario`;
}

export interface PendingDealItem {
  link: DealLink;
  person_id: string | null;
  company_id: string | null;
  name: string;
  company_name: string | null;
  owner_user_id?: string | null;
  owner_team_id?: string | null;
}

export interface PendingDealScope {
  scope?: "mine" | "team" | "all" | "assigned";
  viewerId?: string;
  supportedAeIds?: readonly string[];
  teamId?: string | null;
  scopedRelationshipIds?: ReadonlySet<string>;
}

/** Records a newly converted deal link in the React Query cache so My Pipeline shows it immediately. */
export function recordConvertedDealLink(
  qc: QueryClient,
  item: PendingDealItem,
): void {
  qc.setQueryData<PendingDealItem[]>(crmKeys.pendingDeals, (prev) => {
    const existing = (prev ?? []).filter((x) => x.link.id !== item.link.id);
    return [item, ...existing];
  });
}

function recentItemInScope(
  item: PendingDealItem,
  scopeFilter?: PendingDealScope,
): boolean {
  if (!scopeFilter) return true;
  const { scope, viewerId, supportedAeIds, teamId, scopedRelationshipIds } =
    scopeFilter;
  if (scope === "assigned") return false;
  if (scope === "mine") {
    if (supportedAeIds && supportedAeIds.length > 0) {
      if (item.owner_user_id != null) {
        return supportedAeIds.includes(item.owner_user_id);
      }
      return scopedRelationshipIds
        ? scopedRelationshipIds.has(item.link.relationship_id)
        : true;
    }
    if (item.owner_user_id != null && viewerId) {
      return item.owner_user_id === viewerId;
    }
    if (scopedRelationshipIds && scopedRelationshipIds.size > 0) {
      return scopedRelationshipIds.has(item.link.relationship_id);
    }
    return viewerId ? item.link.created_by === viewerId : true;
  }
  if (scope === "team") {
    if (item.owner_team_id !== undefined) {
      return item.owner_team_id === teamId;
    }
    if (scopedRelationshipIds && scopedRelationshipIds.size > 0) {
      return scopedRelationshipIds.has(item.link.relationship_id);
    }
    return true;
  }
  return true;
}

/** Collects deduplicated pending deal links across relationship records and recently converted links. */
export function collectPendingDealItems(
  pages: readonly {
    person_id: string | null;
    company_id: string | null;
    name: string;
    company_name: string | null;
    deal_links: readonly DealLink[];
    owner_user_id?: string | null;
    owner_team_id?: string | null;
  }[],
  recent: readonly PendingDealItem[] = [],
  loPersonId?: string | null,
  scopeFilter?: PendingDealScope,
): PendingDealItem[] {
  const byId = new Map<string, PendingDealItem>();
  const seenLinkIds = new Set<string>();
  for (const p of pages) {
    for (const link of p.deal_links) {
      seenLinkIds.add(link.id);
    }
    for (const link of pendingDealLinks(p.deal_links)) {
      byId.set(link.id, {
        link,
        person_id: p.person_id,
        company_id: p.company_id,
        name: p.name,
        company_name: p.company_name,
        owner_user_id: p.owner_user_id,
        owner_team_id: p.owner_team_id,
      });
    }
  }
  for (const item of recent) {
    if (
      item.link.core_deal_id === null &&
      !byId.has(item.link.id) &&
      !seenLinkIds.has(item.link.id) &&
      recentItemInScope(item, scopeFilter)
    ) {
      byId.set(item.link.id, item);
    }
  }
  const all = [...byId.values()];
  const filtered = loPersonId
    ? all.filter((x) => x.person_id === loPersonId)
    : all;
  return filtered.sort((a, b) =>
    b.link.created_at.localeCompare(a.link.created_at),
  );
}

/**
 * Pending deal links ("waiting for a scenario") for the top of My Pipeline, using existing
 * `/api/prospecting/relationships`, `/api/prospecting/people/:id` and `/api/prospecting/companies/:id`
 * endpoints plus any deal links converted in this session.
 */
export function usePendingDealLinks(
  me: Me,
  loPersonId?: string | null,
  pipelineScope?: "mine" | "team" | "all" | "assigned",
) {
  const canReadCrm = hasProspecting(me);
  const isRmSupport = me?.user.role === "rm_support";
  const canReadTeam =
    canReadCrm &&
    (hasCap(me, "view_team_relationships") ||
      hasCap(me, "manage_team_relationships") ||
      isRmSupport);
  const canWorkBook =
    canReadCrm && (hasCap(me, "work_own_relationships") || canReadTeam);
  const relScope: "mine" | "book" =
    isRmSupport || (canReadTeam && pipelineScope !== "mine") ? "book" : "mine";
  const selectedTeamId =
    me?.user.team_id && me.teams.some((t) => t.id === me.user.team_id)
      ? me.user.team_id
      : (me?.teams[0]?.id ?? me?.user.team_id ?? null);
  const loPage = useQuery({
    queryKey: crmKeys.person(loPersonId ?? ""),
    queryFn: () => api<PersonPage>(`/api/prospecting/people/${loPersonId}`),
    enabled: canReadCrm && !!loPersonId,
  });
  const rels = useRelationships(relScope, canWorkBook && !loPersonId);
  const rawRelTargets = rels.data ?? [];
  const relTargets =
    !loPersonId && pipelineScope === "assigned"
      ? []
      : !loPersonId && !isRmSupport && pipelineScope === "team"
        ? rawRelTargets.filter((r) => {
            const teamId =
              r.owner_team_id !== undefined
                ? r.owner_team_id
                : r.owner_user_id === me?.user.id
                  ? me?.user.team_id
                  : me?.teams.length === 1
                    ? me.teams[0].id
                    : null;
            return teamId === selectedTeamId;
          })
        : !loPersonId && !isRmSupport && pipelineScope === "mine"
          ? rawRelTargets.filter((r) => r.owner_user_id === me?.user.id)
          : rawRelTargets;
  const fallbackTargets = relTargets
    .filter((r) => !Array.isArray(r.deal_links))
    .slice(0, 25);
  const detailQueries = useQueries({
    queries: fallbackTargets.map((r) =>
      r.person_id
        ? {
            queryKey: crmKeys.person(r.person_id),
            queryFn: () =>
              api<PersonPage>(`/api/prospecting/people/${r.person_id}`),
            enabled: canWorkBook && !loPersonId,
            staleTime: 30_000,
          }
        : {
            queryKey: crmKeys.company(r.company_id ?? ""),
            queryFn: () =>
              api<CompanyPage>(`/api/prospecting/companies/${r.company_id}`),
            enabled: canWorkBook && !loPersonId && !!r.company_id,
            staleTime: 30_000,
          },
    ),
  });
  const recent = useQuery<PendingDealItem[]>({
    queryKey: crmKeys.pendingDeals,
    queryFn: () => [],
    enabled: false,
    initialData: [],
  });

  const pages: {
    person_id: string | null;
    company_id: string | null;
    name: string;
    company_name: string | null;
    deal_links: readonly DealLink[];
    owner_user_id?: string | null;
    owner_team_id?: string | null;
  }[] = [];

  if (loPersonId && loPage.data) {
    pages.push({
      person_id: loPage.data.person.id,
      company_id: loPage.data.person.company_id,
      name: loPage.data.person.full_name,
      company_name: loPage.data.person.company_name,
      deal_links: loPage.data.deal_links,
      owner_user_id: loPage.data.relationship?.owner_user_id ?? null,
      owner_team_id: loPage.data.relationship?.owner_team_id ?? null,
    });
  } else {
    let fallbackIdx = 0;
    for (const r of relTargets) {
      if (Array.isArray(r.deal_links)) {
        pages.push({
          person_id: r.person_id,
          company_id: r.company_id,
          name: r.name,
          company_name: r.company_name,
          deal_links: r.deal_links,
          owner_user_id: r.owner_user_id,
          owner_team_id: r.owner_team_id,
        });
      } else {
        const d = detailQueries[fallbackIdx++]?.data;
        if (d) {
          pages.push({
            person_id: r.person_id,
            company_id: r.company_id,
            name: r.name,
            company_name: r.company_name,
            deal_links: d.deal_links,
            owner_user_id: r.owner_user_id,
            owner_team_id: r.owner_team_id,
          });
        }
      }
    }
  }

  const scopedRelationshipIds = new Set(relTargets.map((r) => r.id));
  return collectPendingDealItems(
    pages,
    recent.data ?? [],
    loPersonId,
    loPersonId
      ? undefined
      : {
          scope: pipelineScope ?? "mine",
          viewerId: me?.user.id,
          supportedAeIds: isRmSupport ? me?.user.supported_ae_ids : undefined,
          teamId: selectedTeamId,
          scopedRelationshipIds,
        },
  );
}

// ── Relationship views (Prospecting) ─────────────────────────────────────────

export type ViewPreset =
  "all" | "call_block" | "ACTIVE" | "TIER2" | "ONBOARDING" | "untouched";

// * Days without a logged touch before a relationship joins the morning call block. A working
// default for the pilot, not an owner rule yet; change it here.
export const STALE_AFTER_DAYS = 14;

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Due or overdue follow-up, never touched, or untouched for STALE_AFTER_DAYS; excluded tiers never. */
export function isStaleOrDue(
  r: RelationshipRow,
  now: Date = new Date(),
): boolean {
  if (r.tier_key === "EXCLUDED") return false;
  if (r.next_follow_up && r.next_follow_up <= ymd(now)) return true;
  if (!r.last_touch_at) return true;
  return (
    (now.getTime() - new Date(r.last_touch_at).getTime()) / 86_400_000 >=
    STALE_AFTER_DAYS
  );
}

/** Hold reason while no current Salesforce protection evidence exists for the relationship. */
export const PROTECTION_NOT_CHECKED =
  "Not cleared to call: Salesforce protection not checked yet";

/**
 * Whether current Salesforce protection evidence clears this relationship for outreach (another AE's
 * active prospecting or recent funded relationship). This is the only gate to change when the
 * Protection Register lands. The register is plan only (docs/PROSPECT-PROTECTION-PLAN.md): there is no
 * protection field, no freshness rule and the funding lookback is unconfirmed. AGENTS.md: stale,
 * incomplete or unknown protection evidence cannot clear outreach, so nothing is cleared yet.
 */
export function protectionCleared(_r: RelationshipRow): boolean {
  return false;
}

/** Why a relationship is due for a touch (overdue or due follow-up, never or long untouched), or null. */
function dueReason(r: RelationshipRow, now: Date): string | null {
  if (!isStaleOrDue(r, now)) return null;
  const today = ymd(now);
  if (r.next_follow_up && r.next_follow_up < today)
    return `Follow-up overdue since ${shortDate(r.next_follow_up)}`;
  if (r.next_follow_up === today) return "Follow-up due today";
  if (!r.last_touch_at) return "No call, email or meeting yet";
  const days = Math.floor(
    (now.getTime() - new Date(r.last_touch_at).getTime()) / 86_400_000,
  );
  return `No contact in ${days} days`;
}

/**
 * The call block gate with protection clearance as an input. Exported for tests only; every caller
 * goes through callBlockReason or callBlockHeldBack, which pass protectionCleared(r). `due` says why
 * the relationship is due for a touch; `held` lists every reason it may not be called, each separately
 * so that clearing one never hides another. Cleared to call means due and nothing held.
 */
export function callBlockGate(
  r: RelationshipRow,
  now: Date,
  protection: boolean,
): { due: string | null; held: string[] } {
  const held: string[] = [];
  if (r.tier_key === "EXCLUDED") held.push("Excluded tier");
  if (r.tier_key === "REVIEW") held.push("Review tier");
  const call = channelState(r.sf_do_not_call);
  if (call === "blocked") held.push("Do Not Call");
  else if (call === "unknown") held.push("Call status unknown");
  if (!protection) held.push(PROTECTION_NOT_CHECKED);
  return { due: dueReason(r, now), held };
}

/**
 * Why a relationship is in the morning call block, or null when it is not: it must be due or stale,
 * outside the Excluded and Review tiers, Salesforce Do Not Call observed off, and cleared by current
 * Salesforce protection evidence. An imported tier or Do Not Call alone is never clearance to call.
 */
export function callBlockReason(
  r: RelationshipRow,
  now: Date = new Date(),
): string | null {
  const g = callBlockGate(r, now, protectionCleared(r));
  return g.held.length === 0 ? g.due : null;
}

export interface HeldBackRow {
  row: RelationshipRow;
  /** Why the relationship is due for a touch. */
  due: string;
  /** Every reason it is not cleared to call. */
  held: string[];
}

/**
 * Due or stale relationships kept out of the call block, with every hold reason, in call block order.
 * Excluded relationships are never due, so they are not listed.
 */
export function callBlockHeldBack(
  list: RelationshipRow[],
  now: Date = new Date(),
): HeldBackRow[] {
  const today = ymd(now);
  return list
    .flatMap((row) => {
      const { due, held } = callBlockGate(row, now, protectionCleared(row));
      return due && held.length > 0 ? [{ row, due, held }] : [];
    })
    .sort((a, b) => compareCallBlock(a.row, b.row, today));
}

const TIER_ORDER: Record<string, number> = {
  ACTIVE: 0,
  ONBOARDING: 1,
  TIER2: 2,
};

/** Call block order: overdue and due follow-ups first, then tier, then the longest without contact. */
function callBlockRank(
  r: RelationshipRow,
  today: string,
): [number, number, string] {
  const due = r.next_follow_up && r.next_follow_up <= today ? 0 : 1;
  const tier = TIER_ORDER[r.tier_key ?? ""] ?? 3;
  return [due, tier, r.last_touch_at ?? ""];
}

function compareCallBlock(
  a: RelationshipRow,
  b: RelationshipRow,
  today: string,
): number {
  const [x, y] = [callBlockRank(a, today), callBlockRank(b, today)];
  return x[0] - y[0] || x[1] - y[1] || x[2].localeCompare(y[2]);
}

/** Saved preset, then a case-insensitive match on name, company, NMLS, email, phone, owner or tier. */
export function filterRelationships(
  list: RelationshipRow[],
  preset: ViewPreset,
  text: string,
  now: Date = new Date(),
): RelationshipRow[] {
  const needle = text.trim().toLowerCase();
  const out = list.filter((r) => {
    if (preset === "call_block" && callBlockReason(r, now) === null)
      return false;
    if (
      (preset === "ACTIVE" || preset === "TIER2" || preset === "ONBOARDING") &&
      r.tier_key !== preset
    )
      return false;
    if (preset === "untouched" && r.last_touch_at !== null) return false;
    if (!needle) return true;
    return [
      r.name,
      r.company_name,
      r.nmls,
      r.email,
      r.phone,
      r.owner_name,
      r.tier_label,
    ].some((v) => v?.toLowerCase().includes(needle));
  });
  if (preset !== "call_block") return out;
  const today = ymd(now);
  return out.sort((a, b) => compareCallBlock(a, b, today));
}

/** The side of the wall the command palette searches: the current page's, if the user may see it. */
export function paletteWorkspace(me: Me, pathname: string): Workspace {
  const spaces = workspacesFor(me);
  const here = workspaceOf(pathname);
  if (spaces.includes(here)) return here;
  // A path the user can't open falls back to Active deals, the home of the deal side.
  return spaces.includes("deals") ? "deals" : (spaces[0] ?? "deals");
}
